import React, { useContext } from 'react';

import { useStore } from 'react-redux';

import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Form, { AutoSave } from '@asteria/component-form';
import { FeatureFlag } from '@asteria/component-tools/featureflag';

import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { cn } from '@asteria/utils-funcs/classes';

import FormUpdater from '../../components/form-updater';
import Header, { HeaderSimple } from '../../components/header';
import { ForecasterContext } from '../../context';
import { useForecastQuery } from '../../hooks';

import CategoryPageContent from './content';

import './styles.scss';

function useForecastData({ selectedScenario }) {
	return useForecastQuery({
		selectedScenario,
		history: {
			fields: `
				date
				types {
					type total currency
					statuses {
						status total currency
						categories {
							_id total currency details { name color }
							tags {
								_id total currency details { name color }
							}
						}
					}
				}
			`,
		},
	});
}

/**
 * @type { React.FC<{ className: string, variant: import('../../types').CategoryPageVariant }> }
 */
const CategoryPage = React.memo((props) => {
	const { className, variant, selectedScenario } = props;
	const { onAction } = useContext(ForecasterContext);

	const store = useStore();

	const [view, setView] = React.useState('graph');

	const data = useForecastData({ selectedScenario });

	const onChangeView = React.useCallback((view) => {
		return () => {
			setView(view);
		};
	}, []);

	const mutation = useMutation({
		mutationFn: async (data) => {
			const changes = data?.saving ?? [];

			const sourceScenarioData =
				ScenarioStore.selectors.sourceScenarioData(store.getState());

			if (changes.length === 0) {
				return;
			}

			return onAction?.('forecaster:operations:save', {
				scenario: {
					_id: sourceScenarioData?._id,
					name: sourceScenarioData?.name,
				},
				data: changes,
				noRedirect: true,
			});
		},
	});

	return (
		<div
			className={cn(
				'asteria-component__forecaster-page',
				'asteria--type-category',
				{ [`asteria--view-${view}`]: view },
				className,
			)}
		>
			<Form onSubmit={mutation.mutateAsync}>
				<FormUpdater
					history={data.history}
					yearHistory={data.yearHistory}
					scenario={data.scenario}
				/>
				{variant !== 'action' ? <AutoSave debounce={1000} /> : null}
				<FeatureFlag feature="forecaster-header-outside">
					<HeaderSimple />
				</FeatureFlag>

				<FeatureFlag feature="forecaster-header-outside" invert>
					<Header />
				</FeatureFlag>
				<CategoryPageContent
					loading={data.loading}
					variant={variant}
					onChangeView={onChangeView}
					view={view}
				/>
			</Form>
		</div>
	);
});

CategoryPage.displayName = 'CategoryPage';

CategoryPage.propTypes = {
	className: PropTypes.string,
	onAction: PropTypes.func,
	onSubmit: PropTypes.func,
	variant: PropTypes.string,
};

export default CategoryPage;
